import { cssEase, GAME_DURATION, GAME_EASE, toMs } from "@/lib/motion"
import { useReducedMotion } from "motion/react"
import { type RefObject, useEffect, useRef, useState } from "react"
import { pegsPassed, SPIN_EASING, SPIN_MS, spinAngle } from "./prize-wheel-geometry"

/** What {@link useWheelSpin} spins: the identity of the spin (null while idle), the rotation it stops at, the slice count, whether it is a win and the end callback. */
export interface WheelSpinOptions<K> {
  spinKey: K | null
  target: number | null
  count: number
  won: boolean
  onSpinEnd: () => void
}

/** The elements a spin drives and where it stands: `instant` when it lands without animating, `landed` once the current spin has stopped. */
export interface WheelSpinState {
  rotorRef: RefObject<HTMLDivElement>
  pointerRef: RefObject<SVGSVGElement>
  bulbsRef: RefObject<SVGGElement>
  instant: boolean
  landed: boolean
}

const TICK_MS = toMs(GAME_DURATION.tick)
const TICK_DEGREES = -18
const TWINKLE_MS = toMs(GAME_DURATION.twinkle)
const CELEBRATE_MS = toMs(GAME_DURATION.celebrate)

function canAnimate(): boolean {
  return typeof Element !== "undefined" && typeof Element.prototype.animate === "function"
}

function elapsedOf(animation: Animation): number {
  const time = animation.currentTime
  return typeof time === "number" ? time : SPIN_MS
}

/** Spins the rotor to `target` with the Web Animations API, clicks the pointer at every peg and twinkles the bulbs meanwhile, then rests the rotor there; lands at once under reduced motion or without the API, and reports the end of each spin once. */
export function useWheelSpin<K>({ spinKey, target, count, won, onSpinEnd }: WheelSpinOptions<K>): WheelSpinState {
  const reduceMotion = useReducedMotion() ?? false
  const instant = reduceMotion || !canAnimate()
  const rotorRef = useRef<HTMLDivElement>(null)
  const pointerRef = useRef<SVGSVGElement>(null)
  const bulbsRef = useRef<SVGGElement>(null)
  const restingAngle = useRef(0)
  const reportedKey = useRef<K | null>(null)
  const latest = useRef({ onSpinEnd, won })
  const [landedKey, setLandedKey] = useState<K | null>(null)

  useEffect(() => {
    latest.current = { onSpinEnd, won }
  })

  useEffect(() => {
    const rotor = rotorRef.current
    if (!rotor || target === null || spinKey === null) return
    const rest = () => {
      restingAngle.current = target % 360
      rotor.style.transform = `rotate(${restingAngle.current}deg)`
    }
    const report = () => {
      if (Object.is(reportedKey.current, spinKey)) return
      reportedKey.current = spinKey
      latest.current.onSpinEnd()
    }
    if (instant) {
      rest()
      report()
      return
    }
    const from = restingAngle.current
    const bulbs = Array.from(bulbsRef.current?.children ?? [])
    let cancelled = false
    let frame = 0
    let tick: Animation | null = null
    const spin = rotor.animate([{ transform: `rotate(${from}deg)` }, { transform: `rotate(${target}deg)` }], {
      duration: SPIN_MS,
      easing: SPIN_EASING,
      fill: "forwards",
    })
    const twinkles = bulbs.map((bulb, index) =>
      bulb.animate([{ opacity: 1 }, { opacity: 0.3 }, { opacity: 1 }], {
        duration: TWINKLE_MS,
        iterations: Infinity,
        delay: (index % 2) * (TWINKLE_MS / 2),
      }),
    )
    let passed = pegsPassed(from, count)
    const follow = () => {
      const elapsed = elapsedOf(spin)
      const now = pegsPassed(spinAngle(from, target, elapsed), count)
      if (now !== passed) {
        passed = now
        tick?.cancel()
        tick =
          pointerRef.current?.animate(
            [{ transform: "rotate(0deg)" }, { transform: `rotate(${TICK_DEGREES}deg)`, offset: 0.3 }, { transform: "rotate(0deg)" }],
            { duration: TICK_MS, easing: cssEase(GAME_EASE.tick) },
          ) ?? null
      }
      if (elapsed < SPIN_MS) frame = requestAnimationFrame(follow)
    }
    frame = requestAnimationFrame(follow)
    spin.finished.then(
      () => {
        if (cancelled) return
        rest()
        spin.cancel()
        twinkles.forEach((twinkle) => twinkle.cancel())
        if (latest.current.won)
          bulbs.forEach((bulb) =>
            bulb.animate([{ opacity: 1 }, { opacity: 0.2 }, { opacity: 1 }], { duration: CELEBRATE_MS, iterations: 4 }),
          )
        setLandedKey(() => spinKey)
        report()
      },
      () => undefined,
    )
    return () => {
      cancelled = true
      cancelAnimationFrame(frame)
      tick?.cancel()
      spin.cancel()
      twinkles.forEach((twinkle) => twinkle.cancel())
    }
  }, [spinKey, target, count, instant])

  return {
    rotorRef,
    pointerRef,
    bulbsRef,
    instant,
    landed: spinKey !== null && (instant || Object.is(landedKey, spinKey)),
  }
}
